import { forwardRef } from 'react';
import type { Ref, PropsWithoutRef, ReactNode, MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import type { ForwardRefForwardPropsComponent, BaseProps, AsProp, TestIdProp } from '../../types';
import { useFullscreenContext } from '../Fullscreen';
import useTestIds from '../../hooks/useTestIds';

import { StyledCardMedia } from './CardMedia';
import { getCardTestIds } from './Card.test-ids';

export interface CardProps extends BaseProps, AsProp, TestIdProp {
  /** Components and content for the Card. */
  children?: ReactNode;
  /**
   * Enables the focus, hover, and active styles.
   * @default false
   * @deprecated
   */
  interactive?: boolean;
  /** Callback for onClick event. */
  onClick?: MouseEventHandler<HTMLElement>;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLElement>;
}

export const StyledCard = styled.article<Partial<CardProps>>(props => {
  const fullscreen = useFullscreenContext();

  const {
    'border-radius': borderRadius,
    'border-color': borderColor,
    background
  } = props.theme.components.card;
  const interactive = props.interactive
    ? css`
        &:hover {
          border: 0;
          box-shadow: 0 0 0 0.0625rem ${borderColor};
        }
        &:focus {
          border: 0;
          box-shadow: 0 0 0 0.125rem ${borderColor};
        }
      `
    : undefined;

  return css`
    background: ${background};

    &:focus {
      outline: none;
    }

    ${interactive}

    ${StyledCardMedia} {
      &:first-child img {
        border-radius: var(--container-border-radius, ${borderRadius})
          var(--container-border-radius, ${borderRadius}) 0 0;
      }
    }

    ${fullscreen?.[0]
      ? css`
          block-size: 100%;
          border-radius: 0;
        `
      : css`
          border-radius: var(--border-radius, var(--container-border-radius));
          --container-border-radius: ${borderRadius};
          * {
            --border-radius: calc(var(--container-border-radius) * 0.5);
          }
        `}
  `;
});

StyledCard.defaultProps = defaultThemeProp;

const Card: ForwardRefForwardPropsComponent<CardProps> = forwardRef(function Card(
  { children, as, onClick, interactive = false, testId, ...restProps }: PropsWithoutRef<CardProps>,
  ref: CardProps['ref']
) {
  const testIds = useTestIds(testId, getCardTestIds);
  const interactiveProps = {
    ...(onClick && {
      onClick,
      tabIndex: 0,
      role: 'button'
    }),
    ...(interactive && {
      tabIndex: 0
    }),
    interactive
  };

  return (
    <Flex
      data-testid={testIds.root}
      role='none'
      container={{ direction: 'column' }}
      as={StyledCard}
      forwardedAs={as}
      ref={ref}
      {...interactiveProps}
      {...restProps}
    >
      {children}
    </Flex>
  );
});

export default Card;
